import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EvolutionTimelineModal from '../evolutions/evolutionTimelineModal';
import EvolutionModal from '../evolutions/evolutionModal';
import SuccessMessage from '../../../../../components/successMessage';
import ErrorMessage from '../../../../../components/errorMessage';
import Loading from '../../../../../components/loading';
import IncomesModal from '../../../../../financial/incomes/incomesModal';
import ProcedureModalToOdonto from '../procedureModalToOdonto';
import ProcedureModalToAesthetic from '../procedureModalToAesthetic';
import RectificationHistoryModal from '../rectification/rectificationHistoryModal';
import { useProcedures } from '../hooks/useProcedures';
import { useProcedureActions } from '../hooks/useProcedureActions';
import { useClinic } from '../../../../../hooks/useClinic';
import { isApproved } from '../../treatmentStatus';

export default function ProcedureList({ treatment, onProcedureSaved, patientId }) {
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [procedures, setProcedures] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [isOdontoModalOpen, setIsOdontoModalOpen] = useState(false);
  const [isAestheticModalOpen, setIsAestheticModalOpen] = useState(false);
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false);
  const [isTimelineModalOpen, setTimelineModalOpen] = useState(false);
  const [isEvolutionModalOpen, setEvolutionModalOpen] = useState(false);
  const [isRectifyModalOpen, setRectifyModalOpen] = useState(false);
  const [isRectificationHistoryOpen, setRectificationHistoryOpen] = useState(false);

  const [openIncomeModal, setOpenIncomeModal] = useState(false);
  const [incomeDescription, setIncomeDescription] = useState('');
  const [incomeAmount, setIncomeAmount] = useState(0);
  const [incomeProfessionalId, setIncomeProfessionalId] = useState(null);

  const {
    procedures: fetchedProcedures,
    isLoading: isProceduresLoading,
    refetch: getProceduresFromTreatment,
  } = useProcedures(treatment?.treatmentId);
  const {
    remove,
    generateCharge: generateChargeRequest,
    finish: finishRequest,
  } = useProcedureActions();
  const { clinic } = useClinic();
  const modules = clinic?.modules ?? [];

  useEffect(() => {
    setProcedures(fetchedProcedures);
  }, [fetchedProcedures]);

  const hasOdonto = modules.includes('ODONTO');
  const hasAesthetic = modules.includes('AESTHETIC');

  const openProcedureModal = () => {
    if (hasOdonto && hasAesthetic) {
      setIsTypeSelectionOpen(true);
    } else if (hasAesthetic) {
      setIsAestheticModalOpen(true);
    } else {
      setIsOdontoModalOpen(true);
    }
  };

  const closeOdontoModal = () => setIsOdontoModalOpen(false);
  const closeAestheticModal = () => setIsAestheticModalOpen(false);

  const openOdontoFromSelection = () => {
    setIsTypeSelectionOpen(false);
    setIsOdontoModalOpen(true);
  };

  const openAestheticFromSelection = () => {
    setIsTypeSelectionOpen(false);
    setIsAestheticModalOpen(true);
  };

  const onSaveSuccess = (message) => {
    setShowSuccessMessage(true);
    setSuccessMessage(message);
    getProceduresFromTreatment(treatment);
    closeOdontoModal();
    closeAestheticModal();
    onProcedureSaved();
  };

  const openActionsMenu = (event, procedure) => {
    setAnchorEl(event.currentTarget);
    setSelectedProcedure(procedure);
  };

  const closeActionsMenu = () => {
    setAnchorEl(null);
  };

  const openEvolutionModal = () => {
    setEvolutionModalOpen(true);
    setAnchorEl(null);
  };

  const onSaveEvolution = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
      getProceduresFromTreatment(treatment);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    setEvolutionModalOpen(false);
  };

  function deleteSelectedProcedure() {
    closeActionsMenu();
    setLoading(true);
    remove(selectedProcedure.id)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        getProceduresFromTreatment(treatment);
        onProcedureSaved();
        setLoading(false);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + returnFromApi);
        setErrorMessage(returnFromApi?.response?.data);
      });
  }

  const saveIncome = (returnFromApi) => {
    setShowSuccessMessage(true);
    setSuccessMessage(returnFromApi.data);
    setOpenIncomeModal(false);
  };

  const generateCharge = () => {
    closeActionsMenu();
    setLoading(true);

    generateChargeRequest(treatment.treatmentId, selectedProcedure.id)
      .then((returnFromApi) => {
        const charge = returnFromApi.data;
        setIncomeAmount(charge.amount);
        setIncomeDescription(charge.description);
        setIncomeProfessionalId(charge.professionalId);
        setLoading(false);
        setOpenIncomeModal(true);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const finishProcedure = () => {
    const procedureId = selectedProcedure.id;
    closeActionsMenu();
    setLoading(true);
    finishRequest(procedureId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        getProceduresFromTreatment(treatment);
        onProcedureSaved();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi?.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openRectifyModal = () => {
    setRectifyModalOpen(true);
    closeActionsMenu();
  };

  const onRectified = (message) => {
    setRectifyModalOpen(false);
    setShowSuccessMessage(true);
    setSuccessMessage(message);
    getProceduresFromTreatment(treatment);
    onProcedureSaved();
  };

  const viewRectifications = () => {
    setRectificationHistoryOpen(true);
    closeActionsMenu();
  };

  const viewEvolutions = () => {
    if (selectedProcedure) {
      setTimelineModalOpen(true);
    }
    closeActionsMenu();
  };

  if (!treatment) {
    return null;
  }

  return (
    <>
      <Loading isToHidden={isLoading === false && isProceduresLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Procedimentos
        </Typography>
        <Tooltip
          title={
            isApproved(treatment)
              ? 'Não é possível adicionar procedimentos a um tratamento aprovado.'
              : ''
          }
        >
          <span>
            <Button
              data-cy="add-procedure-btn"
              variant="outlined"
              onClick={openProcedureModal}
              disabled={isApproved(treatment)}
            >
              Adicionar Procedimento
            </Button>
          </span>
        </Tooltip>
      </Box>

      {procedures.length === 0 ? (
        <Typography align="center" sx={{ py: 4 }}>
          Nenhum procedimento cadastrado ainda.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {procedures.map((procedure) => (
                <Tooltip
                  key={procedure.id}
                  title={
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>
                        <strong>Criado em:</strong> {procedure.createdAt}
                      </span>
                      <span>
                        <strong>Finalizado em:</strong> {procedure.finishedAt}
                      </span>
                    </div>
                  }
                >
                  <TableRow data-cy="procedure-row" hover>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2">{procedure.descricao}</Typography>
                      {procedure.observacao && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {procedure.observacao}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {procedure.dentePorExtenso && (
                          <Tooltip title="Dente">
                            <Chip
                              label={`D${procedure.dentePorExtenso}`}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {procedure.evolucoes && procedure.evolucoes.length > 0 && (
                          <Tooltip title="Quantidade de Evoluções">
                            <Chip
                              label={`E${procedure.evolucoes.length}`}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {procedure.retificado && (
                          <Tooltip title="Procedimento retificado">
                            <Chip
                              label="Retificado"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{procedure.valor}</TableCell>
                    <TableCell>
                      <Chip
                        label={procedure.descricaoDoStatus}
                        color={
                          procedure.status === 'FINALIZADO'
                            ? 'success'
                            : procedure.status === 'EM_ANDAMENTO'
                              ? 'warning'
                              : 'info'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>
                      <IconButton
                        size="small"
                        onClick={(event) => openActionsMenu(event, procedure)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </Tooltip>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeActionsMenu}>
        <MenuItem data-cy="see-evolution-btn" onClick={viewEvolutions}>
          Ver Evoluções
        </MenuItem>
        <span>
          <MenuItem data-cy="add-evolution-btn" onClick={openEvolutionModal}>
            Adicionar Evolução
          </MenuItem>
        </span>
        <Tooltip
          placement="left"
          title={
            selectedProcedure?.status !== 'EM_ANDAMENTO'
              ? 'Só é possível retificar procedimentos em andamento.'
              : ''
          }
        >
          <span>
            <MenuItem
              data-cy="rectify-procedure-btn"
              onClick={openRectifyModal}
              disabled={selectedProcedure?.status !== 'EM_ANDAMENTO'}
            >
              Retificar
            </MenuItem>
          </span>
        </Tooltip>
        {selectedProcedure?.retificado && (
          <MenuItem data-cy="see-rectifications-btn" onClick={viewRectifications}>
            Ver Retificações
          </MenuItem>
        )}
        <span>
          <MenuItem
            data-cy="finish-procedure-btn"
            onClick={finishProcedure}
            disabled={selectedProcedure?.status === 'FINALIZADO'}
          >
            Finalizar
          </MenuItem>
        </span>
        <span>
          <MenuItem onClick={generateCharge}>Gerar Cobrança</MenuItem>
        </span>
        <Tooltip
          placement="left"
          title={
            selectedProcedure?.status === 'FINALIZADO'
              ? 'Não é possível deletar um procedimento finalizado.'
              : ''
          }
        >
          <span>
            <MenuItem
              data-cy="delete-procedure-btn"
              onClick={deleteSelectedProcedure}
              sx={{ color: 'error.main' }}
              disabled={selectedProcedure?.status === 'FINALIZADO'}
            >
              Deletar
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      <Dialog open={isTypeSelectionOpen} onClose={() => setIsTypeSelectionOpen(false)}>
        <DialogTitle>Tipo de Procedimento</DialogTitle>
        <DialogContent>
          <Typography>Selecione o tipo de procedimento que deseja cadastrar:</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTypeSelectionOpen(false)}>Cancelar</Button>
          <Button variant="outlined" onClick={openOdontoFromSelection}>
            Odontológico
          </Button>
          <Button variant="contained" onClick={openAestheticFromSelection}>
            Estético
          </Button>
        </DialogActions>
      </Dialog>

      <ProcedureModalToOdonto
        isOpen={isOdontoModalOpen}
        onClose={closeOdontoModal}
        onSaveSuccess={onSaveSuccess}
        treatment={treatment}
      />

      <ProcedureModalToAesthetic
        isOpen={isAestheticModalOpen}
        onClose={closeAestheticModal}
        onSaveSuccess={onSaveSuccess}
        treatment={treatment}
      />

      <EvolutionTimelineModal
        open={isTimelineModalOpen}
        onClose={() => setTimelineModalOpen(false)}
        procedure={selectedProcedure}
      />

      <EvolutionModal
        open={isEvolutionModalOpen}
        onClose={() => setEvolutionModalOpen(false)}
        onSaveEvolution={onSaveEvolution}
        selectedProcedure={selectedProcedure}
      />

      {isRectifyModalOpen && (
        <ProcedureModalToOdonto
          isOpen
          onClose={() => setRectifyModalOpen(false)}
          onSaveSuccess={onRectified}
          treatment={treatment}
          procedureToRectify={selectedProcedure}
        />
      )}

      {isRectificationHistoryOpen && (
        <RectificationHistoryModal
          open
          onClose={() => setRectificationHistoryOpen(false)}
          procedure={selectedProcedure}
        />
      )}

      {openIncomeModal && (
        <IncomesModal
          open
          onClose={() => setOpenIncomeModal(false)}
          onSaveSuccess={saveIncome}
          incomeId={null}
          incomeDescription={incomeDescription}
          incomeAmount={incomeAmount}
          incomeOrigin={'PROCEDURE'}
          patientId={patientId}
          professionalId={incomeProfessionalId}
        />
      )}
    </>
  );
}
